import { useState, useRef, useEffect } from 'react';
import { Conversation } from '@elevenlabs/client';
import api from '@/utils/ky';
import { AIAKO_FAQ_AGENT_ID } from '@/utils/constants';

export enum LoadingState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ENDING = 'ending',
  ERROR = 'error',
}

export interface TranscriptEntry {
  source: 'user' | 'agent';
  message: string;
  timestamp: Date;
}

async function requestMicPermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(t => t.stop());
    return true;
  } catch {
    return false;
  }
}

export const useAiakoAgentCall = () => {
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.INITIAL);
  const [error, setError] = useState<string>('');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const conversationRef = useRef<Conversation | null>(null);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);
      setError('');
      setTranscript([]);

      const hasMic = await requestMicPermission();
      if (!hasMic) {
        setError('Microphone access is required. Please allow microphone permission and try again.');
        setLoadingState(LoadingState.ERROR);
        return;
      }

      const { signedUrl } = await api
        .get(`api/elevenlabs/signed-url?agent_id=${AIAKO_FAQ_AGENT_ID}`)
        .json<{ signedUrl: string }>();

      const conversation = await Conversation.startSession({
        signedUrl,
        onConnect: () => {
          setLoadingState(LoadingState.READY);
          setIsCallActive(true);
          startTimeRef.current = Date.now();
          callTimerRef.current = setInterval(() => {
            setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
          }, 1000);
        },
        onDisconnect: () => {
          setIsCallActive(false);
          setIsSpeaking(false);
          setLoadingState(LoadingState.INITIAL);
          conversationRef.current = null;
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
          }
        },
        onModeChange: ({ mode }: { mode: string }) => {
          setIsSpeaking(mode === 'speaking');
        },
        onMessage: ({ source, message }: { source: 'ai' | 'user'; message: string }) => {
          if (message) {
            setTranscript(prev => [
              ...prev,
              {
                source: source === 'ai' ? 'agent' : 'user',
                message,
                timestamp: new Date(),
              },
            ]);
          }
        },
        onError: (err: unknown) => {
          console.error('ElevenLabs error:', err);
          setError('Connection error. Please try again.');
          setLoadingState(LoadingState.ERROR);
        },
      });

      conversationRef.current = conversation;
    } catch (err) {
      console.error('Failed to start Aiako FAQ call:', err);
      setError('Failed to connect. Please try again.');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const endCall = () => {
    setLoadingState(LoadingState.ENDING);
    if (conversationRef.current) {
      conversationRef.current.endSession();
      conversationRef.current = null;
    }
    setIsCallActive(false);
    setIsSpeaking(false);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      if (conversationRef.current) conversationRef.current.endSession();
    };
  }, []);

  return {
    loadingState,
    error,
    transcript,
    isCallActive,
    callDuration,
    startCall,
    endCall,
    formatDuration,
    isSpeaking,
  };
};
